import { flatDefaultGroup } from '@Utils/jsonUtils';
import API_SERVICE from '@Service/apiService';
import { triggerIndexingWorker } from '@Service/indexingService';
import { STORAGE_TYPE, removeFromStorage } from '@Service/storageService';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import {
    CLEAR_ERRORS,
    CLAUSE_LIBRARY,
    AUTHENTICATION,
    NOTIFICATION_HANDLER,
    MIX_PANEL
} from '@Constants/types';
import store from '../store';
import { sortClauses } from '@Utils/clausesUtils';

export function getClauseLibraryPromise() {
    return new Promise((resolve, reject) => {
        try {
            API_SERVICE.refresh();
            API_SERVICE.setContentType('application/json');

            store.dispatch({ type: CLEAR_ERRORS });
            store.dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_REQUEST });
            addLog(LOG_TYPE.INFO, 'clauseActions.getClauseLibrary', '/seal-ws/v5/clause-libraries');
            API_SERVICE.SEAL_API.get('/seal-ws/v5/clause-libraries')
                .then(res => {
                    if (res.status === 200) {
                        res.data.items.forEach(item => {
                            // Only fetch the clauses/groups from the default clause library
                            return resolve(item.id);
                        });
                    } else if (res.status === 401) {
                        forceSignOut();
                    } else {
                        const errMessage = `Unexpected HTTP Status code:${res.status}`;
                        addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', errMessage);

                        store.dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload: errMessage
                        });
                    }

                    return reject(res.status);
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', err);
                    if (err.response) {
                        if (err.response.status === 401) {
                            forceSignOut();
                        } else if (err.response.status === 403) {
                            addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', err);

                            // store.dispatch({
                            //     type: CLAUSE_LIBRARY.NO_PERMISSIONS,
                            //     payload: err
                            // });
                        } else {
                            store.dispatch({
                                type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED,
                                payload: err
                            });
                        }
                    } else {
                        store.dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED, payload: err });
                    }

                    return reject(err);
                });
        } catch (err) {
            addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', err);
            store.dispatch({
                type: NOTIFICATION_HANDLER.ERROR,
                payload: err
            });
            return reject(err);
        }
    });
}

function getClauseLibraryStatsForMixPanel(flattenClauseGroups) {
    const flattenClauseGroupsKeys = Object.keys(flattenClauseGroups);

    let maximumDepth = 0;
    let depth = 0;
    if (flattenClauseGroups) {
        flattenClauseGroupsKeys.forEach(key => {
            const { clauseCategoryTaxonomy } = flattenClauseGroups[key];
            if (clauseCategoryTaxonomy) {
                depth = clauseCategoryTaxonomy.split(' > ').length;
                if (depth > maximumDepth) maximumDepth = depth;
            }
        });
    }

    let totalClauseCount = 0;
    if (flattenClauseGroups) {
        flattenClauseGroupsKeys.forEach(key => {
            const { clauses } = flattenClauseGroups[key];
            const clauseCount = clauses ? clauses.length : 0;
            totalClauseCount += clauseCount;
        });
    }

    const mixpanel = {
        totalNumberClauseGroups: Object.keys(flattenClauseGroups).length,
        totalNumberClauses: totalClauseCount,
        maxDepth: maximumDepth
    };

    store.dispatch({ type: MIX_PANEL.MIXPANEL_DATA_CLAUSE_LIBRARY, payload: mixpanel });
}

export function getClauseLibraryByIdPromise(clauseLibraryId) {
    return new Promise((resolve, reject) => {
        const endpointUrl = `/seal-ws/v5/clause-libraries/${clauseLibraryId}?expand=clauses`;
        addLog(LOG_TYPE.INFO, 'clauseActions.getClauseLibrary', endpointUrl);
        API_SERVICE.refresh();
        API_SERVICE.SEAL_API({
            method: 'GET',
            url: endpointUrl
        })
            .then(({ status, data }) => {
                if (status === 200) {
                    // eslint-disable-next-line no-unused-vars
                    const { groups } = data;
                    const flattenMappedClauses = groups.reduce(flatDefaultGroup, {});
                    getClauseLibraryStatsForMixPanel(flattenMappedClauses);
                    store.dispatch({
                        type: CLAUSE_LIBRARY.FETCH_LIBRARY_SUCCESS,
                        payload: {
                            defaultClauseGroups: sortClauses(groups),
                            flattenClauseGroups: flattenMappedClauses
                        }
                    });
                    triggerIndexingWorker(flattenMappedClauses);
                    resolve();
                } else {
                    const errMessage = `Unexpected HTTP Status code:${status}`;
                    addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibraryById', errMessage);

                    store.dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: errMessage
                    });

                    reject(errMessage);
                }
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibraryById', err);
                store.dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED, payload: err });
                reject(err);
            });
    });
}

/* Retrieve all the groups/clauses for the default clause library */
export default function getClauseLibrary() {
    try {
        API_SERVICE.refresh();
        API_SERVICE.setContentType('application/json');

        return dispatch => {
            dispatch({ type: CLEAR_ERRORS });
            dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_REQUEST });
            addLog(LOG_TYPE.INFO, 'clauseActions.getClauseLibrary', '/seal-ws/v5/clause-libraries');
            return API_SERVICE.SEAL_API.get('/seal-ws/v5/clause-libraries')
                .then(res => {
                    if (res.status === 200) {
                        res.data.items.forEach(item => {
                            // Only fetch the clauses/groups from the default clause library
                            dispatch(getClauseLibraryById(item.id));
                        });
                    } else if (res.status === 401) {
                        forceSignOut();
                    } else {
                        const errMessage = `Unexpected HTTP Status code:${res.status}`;
                        addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', errMessage);

                        dispatch({
                            type: NOTIFICATION_HANDLER.ERROR,
                            payload: errMessage
                        });
                    }
                })
                .catch(err => {
                    addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', err);
                    if (err.response) {
                        if (err.response.status === 401) {
                            forceSignOut();
                        } else if (err.response.status === 403) {
                            dispatch({
                                type: CLAUSE_LIBRARY.NO_PERMISSIONS,
                                payload: err
                            });
                        } else {
                            dispatch({
                                type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED,
                                payload: err
                            });
                        }
                    } else {
                        dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED, payload: err });
                    }
                });
        };
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibrary', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return null;
    }
}

/* Get the ClauseGroups for the specified clause library.
   The returned Json data is nested, and needs to be restructured into a linear list of groups
   Recursive calls are made to GetGroups to navigate the ClauseGroup taxonomy, converting it into a linear list 'theGroups'
   Finally, a call to method 'getClauseGroup' is made for each group in the linear list to retrieve the clauses for that group */
function getClauseLibraryById(clauseLibraryId) {
    const endpointUrl = `/seal-ws/v5/clause-libraries/${clauseLibraryId}?expand=clauses`;
    addLog(LOG_TYPE.INFO, 'clauseActions.getClauseLibrary', endpointUrl);
    return dispatch => {
        API_SERVICE.refresh();
        API_SERVICE.SEAL_API({
            method: 'GET',
            url: endpointUrl
        })
            .then(({ status, data }) => {
                if (status === 200) {
                    // eslint-disable-next-line no-unused-vars
                    const { groups } = data;
                    const flattenMappedClauses = groups.reduce(flatDefaultGroup, {});
                    dispatch({
                        type: CLAUSE_LIBRARY.FETCH_LIBRARY_SUCCESS,
                        payload: {
                            defaultClauseGroups: sortClauses(groups),
                            flattenClauseGroups: flattenMappedClauses
                        }
                    });
                    triggerIndexingWorker(flattenMappedClauses);
                } else {
                    const errMessage = `Unexpected HTTP Status code:${status}`;
                    addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibraryById', errMessage);

                    dispatch({
                        type: NOTIFICATION_HANDLER.ERROR,
                        payload: errMessage
                    });
                }
            })
            .catch(err => {
                addLog(LOG_TYPE.ERROR, 'clauseActions.getClauseLibraryById', err);
                dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_FAILED, payload: err });
            });
    };
}

/* If the system has been left idle for too long the auth token will expire.  When this occurs
   clear down the token, reset the login state and force the users tab to the login tab */
function forceSignOut() {
    try {
        removeFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
        removeFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);
        store.dispatch({ type: AUTHENTICATION.LOGOUT_SUCCESS });
        store.dispatch({ type: CLAUSE_LIBRARY.FETCH_LIBRARY_TOKEN_EXPIRED });
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'clauseActions.forceSignOut', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}
